import { Shield, AlertTriangle, Upload, Database, Search } from 'lucide-react';

export interface ThreatItem {
  id: string;
  name: string;
  risk: 'critical' | 'high' | 'medium' | 'low' | 'info';
  description: string;
  indicators: string[];
  mitigations: string[];
}

export interface Subcategory {
  name: string;
  items: ThreatItem[];
}

export interface Category {
  title: string;
  icon: JSX.Element;
  color: string;
  description: string;
  subcategories: Subcategory[];
}

export interface ThreatMatrix {
  [key: string]: Category;
}

export const RISK_VALUES: Record<ThreatItem['risk'], number> = {
  critical: 25,
  high: 20,
  medium: 15,
  low: 5,
  info: 2,
};

export const THREAT_MATRIX: ThreatMatrix = {
  proactive: {
    title: 'Proactive Measures',
    icon: <Shield className="w-5 h-5" />,
    color: 'from-green-500 to-emerald-500',
    description: 'Preventive controls and policies to deter insider threats before they occur',
    subcategories: [
      {
        name: 'Policy & Governance',
        items: [
          {
            id: 'dlp-enforcement',
            name: 'DLP Enforcement',
            risk: 'medium',
            description: 'Data Loss Prevention policies actively blocking unauthorized data transfers across channels.',
            indicators: ['Policy violations logged', 'Blocked transfer attempts', 'User bypass complaints'],
            mitigations: ['Regular policy review', 'User training on approved channels', 'Exception handling process'],
          },
          {
            id: 'prevent-backups',
            name: 'Prevent Backups to Unapproved Storage',
            risk: 'high',
            description: 'Blocking backups to personal or unauthorized cloud storage, including consumer sync clients.',
            indicators: ['Backup attempts to personal drives', 'Shadow IT cloud usage', 'Sync client installations'],
            mitigations: ['Approved backup solutions', 'Cloud Access Security Broker (CASB)', 'Endpoint agent controls'],
          },
          {
            id: 'employee-awareness',
            name: 'Employee Awareness Training',
            risk: 'low',
            description: 'Security awareness training and insider threat education covering behavioural and technical risks.',
            indicators: ['Low training completion rates', 'Poor phishing test results', 'Repeated policy violations'],
            mitigations: ['Regular training cadence', 'Simulated insider threat scenarios', 'Security champions program'],
          },
          {
            id: 'collaboration-hr',
            name: 'Collaboration with HR',
            risk: 'low',
            description: 'Joint security–HR workflows for insider threat management, investigations, and off-boarding.',
            indicators: ['Siloed HR and security teams', 'Delayed off-boarding notifications', 'Uncoordinated investigations'],
            mitigations: ['Regular cross-functional sync', 'Shared investigation playbooks', 'Automatic access revocation on HR action'],
          },
          {
            id: 'collaboration-legal',
            name: 'Collaboration with Legal',
            risk: 'low',
            description: 'Legal coordination ensuring monitoring programmes, evidence handling, and enforcement are compliant.',
            indicators: ['Monitoring without legal review', 'Improper evidence collection', 'Inconsistent disciplinary outcomes'],
            mitigations: ['Legal review of monitoring scope', 'Documented evidence handling procedures', 'Regular policy audits'],
          },
          {
            id: 'rbac',
            name: 'Role-Based Access Control',
            risk: 'medium',
            description: 'Access rights scoped strictly to job roles, with regular certification and automated provisioning.',
            indicators: ['Over-privileged accounts', 'Access creep over tenure', 'Manual provisioning gaps'],
            mitigations: ['Quarterly access reviews', 'Automated role provisioning', 'Least-privilege enforcement'],
          },
          {
            id: 'legal-monitoring',
            name: 'Legal Monitoring Framework',
            risk: 'low',
            description: 'Legal framework governing employee monitoring scope, consent, and privacy compliance across jurisdictions.',
            indicators: ['Undisclosed monitoring activity', 'Privacy regulation gaps', 'Inconsistent enforcement'],
            mitigations: ['Clear monitoring disclosure policy', 'Jurisdiction-specific consent forms', 'Annual legal review'],
          },
          {
            id: 'collaboration-privacy',
            name: 'Privacy Team Collaboration',
            risk: 'low',
            description: 'Ensuring insider threat programmes are designed with privacy-by-design and comply with GDPR, CCPA, and other regulations.',
            indicators: ['No privacy impact assessment on monitoring', 'Data retention policy gaps', 'Excessive data collection'],
            mitigations: ['Privacy-by-design approach', 'Data minimisation in monitoring', 'Regular privacy reviews'],
          },
          {
            id: 'zero-trust-architecture',
            name: 'Zero Trust Architecture',
            risk: 'medium',
            description: 'Never-trust-always-verify model eliminating implicit trust for internal users, devices, and network segments.',
            indicators: ['Legacy perimeter-based security', 'Implicit trust for internal IPs', 'No continuous authentication'],
            mitigations: ['Identity-centric access controls', 'Micro-segmentation', 'Continuous authentication and authorisation'],
          },
          {
            id: 'pam-implementation',
            name: 'Privileged Access Management (PAM)',
            risk: 'high',
            description: 'Secure management, vaulting, and session recording of privileged accounts across IT and OT environments.',
            indicators: ['Shared admin credentials', 'Static long-lived passwords', 'Unmonitored privileged sessions'],
            mitigations: ['Password vaulting with rotation', 'Full session recording', 'Just-in-time privileged access'],
          },
          {
            id: 'background-check-programs',
            name: 'Ongoing Background Check Programme',
            risk: 'medium',
            description: 'Continuous credential monitoring post-hire covering criminal, financial, and clearance changes.',
            indicators: ['No post-hire monitoring', 'Expired security clearances', 'Undisclosed financial distress'],
            mitigations: ['Continuous background monitoring service', 'Periodic re-investigation triggers', 'Financial wellness programme'],
          },
          {
            id: 'vendor-third-party-risk',
            name: 'Vendor & Third-Party Risk Management',
            risk: 'high',
            description: 'Managing insider threat risk from contractors, MSPs, and AI/SaaS vendors with privileged access to systems.',
            indicators: ['Unvetted contractor access', 'Excessive vendor permissions', 'No vendor security assessments'],
            mitigations: ['Vendor security assessments', 'Scoped and time-limited vendor access', 'AI/SaaS vendor data processing agreements'],
          },
          {
            id: 'ai-behavioral-baselines',
            name: 'AI/ML Behavioural Baselines (UEBA)',
            risk: 'low',
            description: 'Machine learning models establishing normal user behaviour patterns to detect anomalies across identity, endpoint, and network telemetry.',
            indicators: ['Manual-only monitoring', 'Static alert rules', 'No behavioural baseline established'],
            mitigations: ['UEBA platform deployment', 'Identity-centric ML model training', 'Peer-group anomaly detection'],
          },
        ],
      },
    ],
  },

  discovery: {
    title: 'Initial Discovery',
    icon: <Search className="w-5 h-5" />,
    color: 'from-blue-500 to-indigo-500',
    description: 'Early indicators and reconnaissance activities signalling elevated insider threat risk',
    subcategories: [
      {
        name: 'Behavioural Indicators',
        items: [
          {
            id: 'browsing-job-sites',
            name: 'Browsing Job Sites',
            risk: 'medium',
            description: 'Employee actively researching job opportunities, indicating potential departure and elevated data exfiltration risk.',
            indicators: ['LinkedIn job searches during work hours', 'Indeed / Glassdoor visits', 'Resume update activity'],
            mitigations: ['Manager check-ins and engagement', 'Proactive retention conversations', 'Structured exit planning process'],
          },
          {
            id: 'disciplinary-article',
            name: 'Employee Under Disciplinary Action',
            risk: 'high',
            description: 'Employee on a PIP or formal disciplinary process — a known precursor to retaliatory insider activity.',
            indicators: ['Active HR disciplinary action', 'Performance improvement plan issued', 'Recent policy violations'],
            mitigations: ['Increased access monitoring', 'Privileged access review', 'Employee support and counselling'],
          },
          {
            id: 'pending-termination',
            name: 'Pending Termination or Resignation',
            risk: 'critical',
            description: 'Employee in notice period or being involuntarily terminated — highest risk window for data theft.',
            indicators: ['Formal resignation received', 'Termination decision made', 'Exit interview scheduled'],
            mitigations: ['Immediate access scope review', 'Legal hold and data preservation', 'Enhanced DLP monitoring for notice period'],
          },
          {
            id: 'corporate-restructure',
            name: 'Corporate Restructure / RIF',
            risk: 'high',
            description: 'Organisational changes such as layoffs, acquisitions, or department closures elevating workforce-wide risk.',
            indicators: ['Layoff announcements', 'Department closure notices', 'Merger or acquisition activity'],
            mitigations: ['Enhanced monitoring during transition periods', 'Clear employee communication', 'Morale and retention programmes'],
          },
          {
            id: 'correspondence-competitors',
            name: 'Correspondence with Competitors',
            risk: 'critical',
            description: 'Detected communication between an employee and a competing organisation — potential IP theft or solicitation.',
            indicators: ['Email to known competitor domains', 'LinkedIn connections to competitor staff', 'Meeting logs with competitors'],
            mitigations: ['Email monitoring with DLP', 'Non-compete and NDA enforcement', 'Legal review on detection'],
          },
          {
            id: 'attempted-access',
            name: 'Attempted Access to Restricted Areas',
            risk: 'high',
            description: 'Repeated failed attempts to access systems, data, or physical areas outside the employee\'s authorisation.',
            indicators: ['Multiple failed access attempts', 'Physical badge violations', 'Lateral system probe activity'],
            mitigations: ['Automated alerting on repeated failures', 'Security team review trigger', 'Access control audit'],
          },
          {
            id: 'financial-distress',
            name: 'Financial Distress Indicators',
            risk: 'high',
            description: 'Employee experiencing significant financial pressure — a key motivator for insider theft or espionage.',
            indicators: ['Bankruptcy or wage garnishment notices', 'Unusual overtime or loan requests', 'EAP financial counselling usage'],
            mitigations: ['Employee assistance programmes', 'Financial counselling referrals', 'Continuous background financial monitoring'],
          },
          {
            id: 'social-engineering-target',
            name: 'Employee Targeted by Social Engineering',
            risk: 'high',
            description: 'External actors targeting employees via phishing, vishing, or social media to harvest credentials or information.',
            indicators: ['Reported phishing or vishing attempts', 'Social media reconnaissance of employee', 'Impersonation of employee identity'],
            mitigations: ['Security awareness training', 'Incident reporting culture', 'Threat intelligence sharing with HR'],
          },
        ],
      },
      {
        name: 'Technical Indicators',
        items: [
          {
            id: 'suspicious-internet',
            name: 'Suspicious Internet Activity',
            risk: 'medium',
            description: 'Unusual browsing activity including dark web access, hacking tool research, or anonymisation tool usage.',
            indicators: ['Dark web forum access', 'Hacking tool download attempts', 'Tor or proxy service usage'],
            mitigations: ['Web content filtering', 'Proxy and anonymiser blocking', 'DNS-based activity monitoring'],
          },
          {
            id: 'activity-outside-scope',
            name: 'Activity Outside Normal Business Scope',
            risk: 'high',
            description: 'Employee accessing systems, data, or performing queries entirely unrelated to their job function.',
            indicators: ['Cross-system access with no business justification', 'Unusual database queries', 'Access to unrelated business units\' data'],
            mitigations: ['Behavioural analytics (UEBA)', 'Manager-reviewed access justification', 'Granular RBAC enforcement'],
          },
          {
            id: 'activity-outside-hours',
            name: 'Activity Outside Normal Business Hours',
            risk: 'medium',
            description: 'System access during nights, weekends, or holidays inconsistent with the employee\'s role or pattern.',
            indicators: ['Late-night or weekend logins', 'Access during public holidays', 'Session from unusual time zones'],
            mitigations: ['Time-based conditional access controls', 'Off-hours access alerting', 'Manager approval workflow for exceptions'],
          },
          {
            id: 'suspicious-creation',
            name: 'Suspicious New Account Creation',
            risk: 'high',
            description: 'Unauthorised creation of service, admin, or backdoor accounts to maintain persistence.',
            indicators: ['Unexpected service accounts created', 'New admin accounts outside change control', 'Dormant backdoor accounts'],
            mitigations: ['Account creation monitoring and alerting', 'Change control approval workflows', 'Regular orphaned account audits'],
          },
          {
            id: 'correspondence-employee',
            name: 'Correspondence with Former Employees',
            risk: 'medium',
            description: 'Communication with departed colleagues who may be soliciting information for a competitor.',
            indicators: ['Email threads with former staff', 'File-sharing invites to old email addresses', 'Information requests from ex-employees'],
            mitigations: ['Email monitoring for known former-employee domains', 'Access termination verification', 'Legal review where appropriate'],
          },
          {
            id: 'modifying-logs',
            name: 'Modifying or Deleting Audit Logs',
            risk: 'critical',
            description: 'Deliberate tampering with audit trails to cover insider activity — a strong indicator of malicious intent.',
            indicators: ['Log deletion events', 'Timestamp manipulation', 'Missing log entries or gaps'],
            mitigations: ['Immutable append-only logging', 'Off-system log forwarding to SIEM', 'Alerting on log gap or deletion events'],
          },
          {
            id: 'use-of-offensive',
            name: 'Use of Offensive or Malicious Tools',
            risk: 'critical',
            description: 'Deploying penetration testing tools, exploits, or malware within the corporate environment.',
            indicators: ['Known offensive tool signatures', 'Script or exploit execution events', 'Internal exploitation attempts'],
            mitigations: ['EDR with behavioural detection', 'Application allow-listing', 'Network-level anomaly monitoring'],
          },
          {
            id: 'multiple-employees',
            name: 'Multiple Employees Leaving to Same Company',
            risk: 'high',
            description: 'A pattern of staff departing to the same competitor — possible organised IP theft or talent poaching.',
            indicators: ['LinkedIn updates showing common destination', 'Exit interview intelligence', 'Industry network reporting'],
            mitigations: ['Non-compete enforcement review', 'Targeted retention programmes', 'Legal counsel engagement'],
          },
          {
            id: 'network-reconnaissance',
            name: 'Unusual Internal Network Reconnaissance',
            risk: 'critical',
            description: 'Internal scanning, mapping, or enumeration activities by an employee without authorisation.',
            indicators: ['Port scanning from employee endpoints', 'Network topology mapping attempts', 'Service and host enumeration'],
            mitigations: ['IDS/IPS alerting on internal scans', 'Network segmentation', 'Immediate security investigation trigger'],
          },
          {
            id: 'code-repository-abuse',
            name: 'Code Repository Abuse',
            risk: 'high',
            description: 'Excessive or unauthorised access to source code repositories — bulk cloning or historical access beyond role.',
            indicators: ['Bulk git clone operations', 'Repo access outside assigned projects', 'Historical branch and archive downloads'],
            mitigations: ['Repository access scoping by team', 'Clone volume alerting', 'Version control audit logging'],
          },
          {
            id: 'vpn-remote-anomalies',
            name: 'VPN & Remote Access Anomalies',
            risk: 'medium',
            description: 'Unusual patterns in VPN or remote access — concurrent sessions, geo-inconsistencies, or banned locations.',
            indicators: ['Simultaneous sessions from different locations', 'Connection from banned countries', 'Unusual session durations'],
            mitigations: ['Geo-location conditional access', 'Concurrent session limits', 'Multi-factor authentication on all remote access'],
          },
        ],
      },
    ],
  },

  collection: {
    title: 'Collection',
    icon: <Database className="w-5 h-5" />,
    color: 'from-purple-500 to-pink-500',
    description: 'Gathering and staging of sensitive data prior to exfiltration',
    subcategories: [
      {
        name: 'Data Gathering',
        items: [
          {
            id: 'downloads-email',
            name: 'Downloads from Email / IM / Chat',
            risk: 'medium',
            description: 'Bulk saving of attachments and files from email, Slack, Teams, or other communication platforms.',
            indicators: ['Large attachment downloads', 'Bulk file saves from chat platforms', 'Sensitive file type downloads'],
            mitigations: ['DLP scanning on communication platforms', 'Attachment size and type limits', 'Communication content inspection'],
          },
          {
            id: 'downloads-internal',
            name: 'Downloads from Internal File Shares',
            risk: 'high',
            description: 'Mass downloading from SharePoint, network drives, or internal document repositories.',
            indicators: ['Bulk folder operations', 'Historical document access', 'Download volume spikes'],
            mitigations: ['Download volume alerting', 'DLP on file share platforms', 'Manager approval for bulk exports'],
          },
          {
            id: 'screenshots',
            name: 'Screenshots of Sensitive Content',
            risk: 'medium',
            description: 'Capturing sensitive information via screen capture tools, mobile phone cameras, or screen recording.',
            indicators: ['Screenshot tool usage on restricted screens', 'Mobile devices in secure areas', 'Screen recording processes'],
            mitigations: ['DLP screen capture controls', 'Document watermarking', 'Restricted device zones'],
          },
          {
            id: 'copying-backups',
            name: 'Copying System Backups',
            risk: 'critical',
            description: 'Accessing and exporting system, database, or VM backups — often containing comprehensive data snapshots.',
            indicators: ['Backup file access outside maintenance windows', 'Large archive transfers', 'Backup storage mount events'],
            mitigations: ['Backup encryption at rest', 'Strict access controls on backup infrastructure', 'Audit logging of all backup access'],
          },
          {
            id: 'downloads-sftp',
            name: 'Downloads from SFTP / Cloud Storage',
            risk: 'high',
            description: 'Downloading corporate data from cloud storage buckets or SFTP servers at unusual volume or frequency.',
            indicators: ['High-volume cloud API downloads', 'SFTP session with large transfers', 'Bucket policy changes before download'],
            mitigations: ['CASB controls on cloud platforms', 'API rate limiting and monitoring', 'Data classification enforcement'],
          },
          {
            id: 'downloads-imds',
            name: 'Cloud IMDS Credential Harvesting',
            risk: 'high',
            description: 'Querying cloud Instance Metadata Service to extract temporary credentials for lateral movement or data access.',
            indicators: ['IMDS endpoint queries from unexpected hosts', 'Credential extraction patterns', 'Unusual IAM role assumption events'],
            mitigations: ['IMDSv2 enforcement', 'Network controls restricting IMDS access', 'Credential usage anomaly monitoring'],
          },
          {
            id: 'printing',
            name: 'High-Volume or After-Hours Printing',
            risk: 'medium',
            description: 'Physical printing of sensitive documents — particularly large jobs, outside business hours, or to remote printers.',
            indicators: ['Large print job volumes', 'After-hours print activity', 'Remote or personal printer usage'],
            mitigations: ['Print job tracking and logging', 'Document watermarking', 'Secure print release (PIN required)'],
          },
          {
            id: 'use-of-file-share',
            name: 'Use of Unauthorised File Share Sites',
            risk: 'high',
            description: 'Using consumer file sharing services (Dropbox, WeTransfer, Google Drive personal) to stage corporate data.',
            indicators: ['Traffic to consumer file sharing domains', 'WeTransfer or similar upload events', 'Personal cloud sync client active'],
            mitigations: ['Consumer file share site blocking', 'DLP rules on egress traffic', 'Approved enterprise alternatives'],
          },
          {
            id: 'api-abuse',
            name: 'API Abuse for Bulk Data Collection',
            risk: 'high',
            description: 'Excessive or scripted API calls to extract large datasets beyond normal business usage patterns.',
            indicators: ['API call volume spikes', 'Automated scripting signatures', 'Rate limit threshold hits'],
            mitigations: ['API rate limiting per user/role', 'Usage anomaly monitoring', 'Strong API authentication logging'],
          },
          {
            id: 'mobile-device-syncing',
            name: 'Mobile Device Data Syncing',
            risk: 'medium',
            description: 'Synchronising corporate data to personal mobile devices via cloud backup or MDM bypass.',
            indicators: ['iCloud or Google account sync from managed devices', 'Personal device corporate app data transfer', 'MDM policy bypass attempts'],
            mitigations: ['Mobile device management (MDM) enforcement', 'Sync restrictions to personal accounts', 'Data containerisation (MAM)'],
          },
          {
            id: 'database-query-abuse',
            name: 'Database Query Abuse',
            risk: 'critical',
            description: 'Unusually large or broad database queries — full table dumps, bulk SELECTs, or export operations on sensitive data.',
            indicators: ['Bulk SELECT on entire tables', 'Database export operations', 'Query patterns deviating from role baseline'],
            mitigations: ['Database activity monitoring (DAM)', 'Row-level query volume alerting', 'Least-privilege database roles'],
          },
          {
            id: 'version-control-abuse',
            name: 'Version Control System Abuse',
            risk: 'high',
            description: 'Downloading entire repositories, historical versions, or generating archives of codebases beyond role scope.',
            indicators: ['Full repository archive generation', 'Historical branch checkout outside project scope', 'Mass clone operations'],
            mitigations: ['Repository-level access monitoring', 'Archive and export alerting', 'Clone volume controls'],
          },
          {
            id: 'email-forwarding-rules',
            name: 'Email Auto-Forwarding Rules',
            risk: 'high',
            description: 'Creating hidden rules to automatically forward emails to external addresses — often for persistent data collection.',
            indicators: ['Auto-forward rules to non-corporate addresses', 'Hidden or obfuscated inbox rules', 'Rules created outside working hours'],
            mitigations: ['Exchange / M365 rule monitoring', 'Blocking external auto-forwarding at mail gateway', 'Admin alerting on rule creation'],
          },
          {
            id: 'shadow-ai-processing',
            name: 'Shadow AI Data Processing',
            risk: 'high',
            description: 'Employees uploading sensitive corporate data to unauthorised AI tools (public ChatGPT, Gemini, Claude.ai) for processing — deliberately or inadvertently exfiltrating data to third-party AI providers.',
            indicators: ['Browser requests to public AI tool domains with large payloads', 'File uploads to AI service endpoints', 'AI tool usage outside approved enterprise platforms'],
            mitigations: ['Approved AI tool policy with acceptable use guidelines', 'Web proxy monitoring for AI service domains', 'CASB controls blocking non-enterprise AI platforms', 'Employee AI literacy training'],
          },
        ],
      },
    ],
  },

  exfiltration: {
    title: 'Exfiltration',
    icon: <Upload className="w-5 h-5" />,
    color: 'from-orange-500 to-red-500',
    description: 'Methods used to remove sensitive data from the organisation',
    subcategories: [
      {
        name: 'Data Exfiltration Methods',
        items: [
          {
            id: 'upload-3rdparty',
            name: 'Upload to Third-Party Service',
            risk: 'critical',
            description: 'Uploading corporate data to external cloud services, personal storage, or public repositories.',
            indicators: ['Personal cloud storage upload events', 'Public code repo commits with proprietary code', 'File sharing site upload traffic'],
            mitigations: ['URL filtering and CASB deployment', 'DLP blocking on egress uploads', 'Network proxy inspection'],
          },
          {
            id: 'use-removable',
            name: 'Removable Storage Devices',
            risk: 'high',
            description: 'USB drives, external hard drives, SD cards, or other removable media used to copy data.',
            indicators: ['USB device connection events', 'Large file copy operations to removable media', 'Encryption bypass on endpoint'],
            mitigations: ['USB port blocking or allowlisting', 'Endpoint device control software', 'Removable media encryption enforcement'],
          },
          {
            id: 'external-email',
            name: 'External Email with Attachment',
            risk: 'high',
            description: 'Sending sensitive files to personal email accounts, competitor domains, or obfuscated external addresses.',
            indicators: ['Attachments to personal email domains', 'Large attachment egress', 'Password-protected or encrypted files to unknown recipients'],
            mitigations: ['Email DLP with content inspection', 'Attachment type and size enforcement', 'External recipient domain allowlisting'],
          },
          {
            id: 'upload-sftp',
            name: 'Upload to Personal SFTP / Cloud Server',
            risk: 'critical',
            description: 'Transferring data to personally controlled servers, VPS instances, or cloud accounts.',
            indicators: ['Outbound SFTP connections to non-corporate servers', 'Personal cloud provider API traffic', 'VPS IP connections from corporate endpoints'],
            mitigations: ['Network egress monitoring', 'Firewall rules blocking unapproved SFTP destinations', 'Cloud traffic inspection'],
          },
          {
            id: 'airdrop-nearby',
            name: 'AirDrop / Nearby Share Transfer',
            risk: 'medium',
            description: 'Using AirDrop, Android Nearby Share, or Bluetooth file transfer to move data to personal devices.',
            indicators: ['Bluetooth device pairing events', 'AirDrop activity logs on managed endpoints', 'Personal device proximity to secure systems'],
            mitigations: ['Bluetooth and AirDrop policy controls via MDM', 'Physical security awareness', 'Mobile device management enforcement'],
          },
          {
            id: 'steganography',
            name: 'Steganography for Data Hiding',
            risk: 'high',
            description: 'Encoding sensitive data hidden within seemingly benign image, audio, or document files for covert exfiltration.',
            indicators: ['Unusually large image files with minimal visual content', 'Non-standard file metadata', 'Modified file header signatures'],
            mitigations: ['Deep content inspection on egress files', 'Metadata analysis tooling', 'File anomaly detection'],
          },
          {
            id: 'dns-tunneling',
            name: 'DNS Tunnelling',
            risk: 'critical',
            description: 'Encoding and exfiltrating data within DNS query and response traffic to bypass traditional security controls.',
            indicators: ['Unusually high DNS query volume', 'Abnormally long DNS record names', 'Non-standard record types in use'],
            mitigations: ['DNS traffic monitoring and analysis', 'DNS query volume alerting', 'Recursive DNS filtering'],
          },
          {
            id: 'social-media-posting',
            name: 'Social Media Data Leakage',
            risk: 'high',
            description: 'Posting proprietary information, screenshots, or coded messages to social platforms.',
            indicators: ['Corporate documents visible in employee posts', 'Coded or obfuscated messages', 'Sensitive metadata in shared images'],
            mitigations: ['Social media monitoring tools', 'Employee social media policy', 'DLP controls on image and document sharing'],
          },
          {
            id: 'qr-code-generation',
            name: 'QR Code Data Encoding',
            risk: 'medium',
            description: 'Encoding sensitive corporate data into QR codes for physical or digital capture on a personal device.',
            indicators: ['QR code generation software on corporate endpoints', 'QR scanning activity on managed devices', 'Encoded data patterns in printed materials'],
            mitigations: ['QR code generation tool restrictions', 'Mobile device scanning policy', 'Physical document controls in secure areas'],
          },
          {
            id: 'covert-channels',
            name: 'Covert Channel Communications',
            risk: 'critical',
            description: 'Hiding data within legitimate protocol traffic — HTTP headers, ICMP payloads, timing channels — to evade detection.',
            indicators: ['Protocol anomalies in allowed traffic', 'Unusual payload sizes in standard protocols', 'Timing-based data transmission patterns'],
            mitigations: ['Deep packet inspection', 'Protocol behaviour baselining', 'Network anomaly detection'],
          },
          {
            id: 'ai-model-exfiltration',
            name: 'AI Model & Training Data Theft',
            risk: 'critical',
            description: 'Theft of proprietary AI models, fine-tuned weights, training datasets, or prompt libraries representing significant R&D investment and competitive advantage.',
            indicators: ['Large model file downloads from ML infrastructure', 'Bulk cloning of ML code repositories', 'Unauthorised access to training data stores', 'Unusual model registry export activity'],
            mitigations: ['AI/ML artifact access controls and monitoring', 'Training data classification and DLP', 'Model watermarking and fingerprinting', 'ML pipeline access scoping'],
          },
        ],
      },
    ],
  },

  impact: {
    title: 'Business Impact',
    icon: <AlertTriangle className="w-5 h-5" />,
    color: 'from-red-500 to-rose-500',
    description: 'Consequences and damage resulting from successful insider threat activity',
    subcategories: [
      {
        name: 'Direct Impact',
        items: [
          {
            id: 'bulk-delete',
            name: 'Bulk File or Database Deletion',
            risk: 'critical',
            description: 'Mass deletion of critical files, records, or databases — typically as sabotage during or after termination.',
            indicators: ['Bulk delete operations in file systems', 'Database DROP or TRUNCATE events', 'Recycle bin or trash volume spikes'],
            mitigations: ['Immutable backup systems', 'Real-time deletion alerting', 'Documented recovery procedures'],
          },
          {
            id: 'destruction-physical',
            name: 'Physical Device Destruction',
            risk: 'critical',
            description: 'Physical destruction or disposal of devices containing sensitive data to prevent forensic recovery.',
            indicators: ['Missing inventory items', 'Damaged equipment returned', 'Unauthorised device disposal events'],
            mitigations: ['Asset tracking and tamper detection', 'Full disk encryption on all devices', 'Remote wipe capability'],
          },
          {
            id: 'changing-account',
            name: 'Service Account Credential Change',
            risk: 'high',
            description: 'Maliciously modifying credentials for shared or service accounts to disrupt operations or deny access.',
            indicators: ['Service account password changes outside maintenance windows', 'Service failures following credential changes', 'Locked-out operational accounts'],
            mitigations: ['Strict change control for service accounts', 'Password vaulting with approval workflows', 'Multi-approver changes for critical credentials'],
          },
          {
            id: 'malicious-changes',
            name: 'Malicious System or Configuration Changes',
            risk: 'critical',
            description: 'Sabotage through unauthorised configuration changes, code modifications, or logic bomb deployment.',
            indicators: ['Configuration changes outside change control', 'Scheduled task or cron job modifications', 'Code changes without peer review'],
            mitigations: ['Change detection and integrity monitoring', 'Mandatory code review', 'Rollback and recovery procedures'],
          },
          {
            id: 'malicious-social',
            name: 'Malicious Social Media Campaign',
            risk: 'high',
            description: 'Deliberate reputational damage through social media — posting confidential information, defamatory content, or misleading disclosures.',
            indicators: ['Negative posts referencing internal information', 'Confidential data shared publicly', 'Coordinated negative review activity'],
            mitigations: ['Social media monitoring and alerting', 'Crisis communications response plan', 'Legal action procedures'],
          },
          {
            id: 'misappropriations',
            name: 'Misappropriation of Physical Assets',
            risk: 'high',
            description: 'Theft of physical assets — hardware, licensed software, or equipment — for personal use or resale.',
            indicators: ['Missing hardware inventory', 'Software licence over-utilisation', 'Unaccounted equipment sign-outs'],
            mitigations: ['Asset management system with audit trails', 'Regular physical inventory reconciliation', 'Secure equipment sign-out process'],
          },
        ],
      },
      {
        name: 'Operational Impact',
        items: [
          {
            id: 'anomalous-activity',
            name: 'Anomalous Time / Frequency Activity',
            risk: 'medium',
            description: 'Statistical deviations in user behaviour patterns — sudden spikes, unusual sequences, or pattern breaks.',
            indicators: ['Statistical outliers in user activity', 'Sudden behavioural change from baseline', 'Activity frequency spikes'],
            mitigations: ['UEBA platform with peer-group analytics', 'Automated baselining and alerting', 'ML-driven anomaly detection'],
          },
          {
            id: 'misuse-assets',
            name: 'Misuse of Corporate Resources',
            risk: 'high',
            description: 'Using company compute, network, or licensed tools for personal gain — cryptocurrency mining, side business operations.',
            indicators: ['High CPU/GPU utilisation on endpoints', 'Network traffic to crypto mining pools', 'Personal project code on corporate systems'],
            mitigations: ['Endpoint resource monitoring', 'Acceptable use policy with enforcement', 'Regular software and process audits'],
          },
          {
            id: 'expanding-internal',
            name: 'Unauthorised Privilege Escalation',
            risk: 'high',
            description: 'Self-granting of elevated permissions or modifying role assignments to access data beyond authorisation.',
            indicators: ['Unexpected permission changes on user accounts', 'Admin group membership modifications', 'Elevated role self-assignment'],
            mitigations: ['PAM solutions with approval workflows', 'Privileged role change alerting', 'Regular privilege certification'],
          },
          {
            id: 'insider-trading',
            name: 'Insider Trading Violations',
            risk: 'critical',
            description: 'Using material non-public information for personal financial gain through securities trading.',
            indicators: ['Unusual securities trades before major announcements', 'Access to earnings or M&A data correlated with trades', 'Tipping off third parties'],
            mitigations: ['Defined trading window controls', 'Compliance monitoring of trade activity', 'Pre-clearance requirements for executives'],
          },
          {
            id: 'use-offensive-malicious',
            name: 'Internal Deployment of Malicious Tools',
            risk: 'critical',
            description: 'Installing or deploying malware, ransomware, or offensive security tools within the corporate environment.',
            indicators: ['Malware signatures on corporate endpoints', 'Offensive tool execution events', 'Internal network scanning or exploitation'],
            mitigations: ['EDR with behavioural detection', 'Network segmentation limiting blast radius', 'Zero trust controls on lateral movement'],
          },
          {
            id: 'exposures-sensitive',
            name: 'Exposure of Classified or Sensitive Information',
            risk: 'critical',
            description: 'Unauthorised public or third-party disclosure of confidential, classified, or regulated data.',
            indicators: ['Sensitive data appearing in public sources', 'Media or analyst reports citing internal data', 'Regulatory notification triggers'],
            mitigations: ['Data classification and handling controls', 'Encryption of sensitive data at rest and in transit', 'Access controls scoped to need-to-know'],
          },
          {
            id: 'tampering-security',
            name: 'Tampering with Physical Security Devices',
            risk: 'high',
            description: 'Disabling, obscuring, or bypassing cameras, access control systems, or alarms to facilitate physical access.',
            indicators: ['Camera blind spots or outages', 'Badge cloning or duplication attempts', 'Lock or alarm bypass events'],
            mitigations: ['Tamper detection on physical security hardware', 'Redundant overlapping camera coverage', 'Regular physical security inspection'],
          },
          {
            id: 'unauthorized-entry',
            name: 'Unauthorised Physical Entry',
            risk: 'medium',
            description: 'Tailgating, piggybacking, or social engineering entry into restricted physical areas.',
            indicators: ['Tailgating events at access control points', 'Visitor management policy bypass', 'Badge sharing between employees'],
            mitigations: ['Mantrap doors at secure access points', 'Visitor management system', 'Security awareness on tailgating'],
          },
        ],
      },
      {
        name: 'Compliance & Legal Impact',
        items: [
          {
            id: 'regulatory-violations',
            name: 'Regulatory Compliance Violations',
            risk: 'critical',
            description: 'Insider-caused breaches of GDPR, HIPAA, SOX, PCI-DSS, or other applicable regulatory requirements.',
            indicators: ['Compliance audit findings', 'Regulatory notice or investigation', 'Data handling policy breaches'],
            mitigations: ['Continuous compliance monitoring', 'Regular internal audit programme', 'Automated policy enforcement'],
          },
          {
            id: 'intellectual-property-theft',
            name: 'Intellectual Property Theft',
            risk: 'critical',
            description: 'Theft of patents, trade secrets, proprietary R&D data, or strategic plans for competitor use or personal gain.',
            indicators: ['Unusual access to R&D or patent databases', 'Bulk download of proprietary research', 'Trade secret information exposure'],
            mitigations: ['IP classification and access controls', 'Legal protections — NDAs, IP assignment agreements', 'Export control monitoring'],
          },
          {
            id: 'customer-data-breaches',
            name: 'Customer Data Breach',
            risk: 'critical',
            description: 'Unauthorised access, theft, or exposure of customer PII, financial records, or health information.',
            indicators: ['Customer database bulk access', 'PII file downloads outside normal scope', 'Payment data exposure events'],
            mitigations: ['Data encryption and tokenisation', 'Customer data access monitoring with alerting', 'Breach response plan and notification procedures'],
          },
          {
            id: 'supply-chain-disruption',
            name: 'Supply Chain Disruption',
            risk: 'high',
            description: 'Insider sabotage of vendor relationships, contract terms, or supply chain operations causing downstream business impact.',
            indicators: ['Vendor communication tampering', 'Unauthorised contract modifications', 'Unexplained supply disruptions'],
            mitigations: ['Vendor management controls', 'Contract change approval workflows', 'Supply chain resilience planning'],
          },
          {
            id: 'competitive-intelligence-loss',
            name: 'Competitive Intelligence Loss',
            risk: 'high',
            description: 'Theft or leakage of strategic plans, pricing data, or market intelligence to competitors.',
            indicators: ['Strategic planning document access outside leadership', 'Pricing data downloads before contract renewals', 'Market research bulk export'],
            mitigations: ['Need-to-know access controls for strategic data', 'Information classification programme', 'Competitive intelligence monitoring'],
          },
        ],
      },
    ],
  },
};

export const ALL_THREATS: ThreatItem[] = Object.values(THREAT_MATRIX).flatMap((category) =>
  category.subcategories.flatMap((sub) => sub.items)
);

export const MAX_POSSIBLE_SCORE: number = ALL_THREATS.reduce(
  (total, threat) => total + (RISK_VALUES[threat.risk] ?? 0),
  0
);
